import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog' 
import Button from '@/components/Button'
import { Input } from '@/components/ui/input'  
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage
} from '@/components/ui/form'
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover" 
import { z } from 'zod' 
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'  
import { usePDVInMarketResearchQuery } from '@/queries/usepdvInMarketResearchQuery'
import VirtualizedCommand from '@/components/VirtualizedCommand'
import { useEffect, useRef, useState } from 'react'
import { TypographyXS } from '@/components/ui/typography'
import { useProductFromPDVMarketResearchQuery } from '@/queries/useProductFromPDVMarketResearchQuery'
import { useOnlineStatus } from '@/hooks/useOnlineStatus'  
import { useOffline } from '@/context/OfflineContext'
import { useToast } from '@/components/ui/use-toast'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createResearchedProduct } from '@/api/business/marketResearch'
import { MarketResearch } from '@/types/MarketResearch'
import { ResearchedProductKeys } from '@/queries/useResearchedProductQuery'
import imageCompression from "browser-image-compression"
import { useProductInMarketResearchQuery } from '@/queries/useProductInMarketResearchQuery'

interface AddDialogProps {
    isOpen: boolean
    onClose: () => void
    MarketResearch: MarketResearch
}

interface ProductsSelect {
    SK_PRODUTO: string
    DS_PRODUTO: string
}

const AddProductMarketResearchDialog = ({ isOpen, onClose, MarketResearch }: AddDialogProps) => { 
    const queryClient = useQueryClient() 
    const currentLocation = useRef({} as GeolocationPosition);
    const [PDV, setPDV] = useState(0)
    const [Product, setProduct] = useState('')
    const [ProductSelect, setProductSelect] = useState<ProductsSelect[]>([])
    const [status, setStatus] = useState(false)
    const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"]
    const online = useOnlineStatus();
    const { toast } = useToast() 
    const { addItem } = useOffline();
    console.log(PDV, Product)
    const { data: DataPDV } = usePDVInMarketResearchQuery(MarketResearch.SK_PESQUISA)
    const { data: DataProduct } = useProductInMarketResearchQuery(MarketResearch.SK_PESQUISA)
    const { data: DataProductFromPDV } = useProductFromPDVMarketResearchQuery(MarketResearch.SK_PESQUISA, PDV, status)

    const schema = z.object({
        SK_CLIENTE: z.number().min(1, {
            message: 'A revenda é obrigatório',
        }),
        SK_PRODUTO: z.string().min(1, {
            message: 'O produto é obrigatório',
        }),
        VL_PRECO_COMPRA: z.number(),
        QTD_VOLUME_COMPRA: z.number(),
        VL_PRECO_VENDA: z.number(),
        IMAGEM: MarketResearch.DD_COLETA_IMAGEM == 1 ? z.any()
            .refine((files) => files && files.length > 0,
                'A imagem é obrigatória'
            )
            .refine((files) =>
                !files || files.length === 0 || ACCEPTED_IMAGE_TYPES.includes(files[0]?.type),
            "Formato de imagem inválido"
            )
            : z.any()
                .optional()
                .refine((files) =>
                    !files || files.length === 0 || ACCEPTED_IMAGE_TYPES.includes(files[0]?.type),
                "Formato de imagem inválido"
            )
    });

    const form = useForm<z.infer<typeof schema>>({
        resolver: zodResolver(schema),
        defaultValues: {  
            SK_PRODUTO: '', 
            VL_PRECO_COMPRA: 0,
            QTD_VOLUME_COMPRA: 0,
            VL_PRECO_VENDA: 0
        },
    })

    const handleSelectProduct = () => {
        setStatus(true)
    }

    const formatCurrencyBR = (value: number | string) => {
        if (!value) return ""

        const number =
            typeof value === "string"
            ? Number(value.replace(/\D/g, "")) / 100
            : value

        return number.toLocaleString("pt-BR", {
            style: "currency",
            currency: "BRL",
        })
    }

    const parseCurrencyBR =  (value: string) => {
        return Number(value.replace(/\D/g, "")) / 100
    }

    const showLocationFailedAlert = (data: z.infer<typeof schema>) => {
        toast({
                title: 'Permissão negada pelo usuário.',
                description: "A aplicação precisa da permissão de localização para funcionamento, por favor ative a localização.",
                variant: 'destructive',
            })
        
        onSave(data)
    }

    const onSubmit = (data: z.infer<typeof schema>) => {  
        navigator.geolocation.getCurrentPosition((position) => {
            currentLocation.current = position; 
            onSave(data)
        }, () => showLocationFailedAlert(data)); 
        form.reset();
    }

    const { mutate, isPending } = useMutation({
        mutationFn: createResearchedProduct,
        onSuccess: () => { 
            queryClient.invalidateQueries({ queryKey: ResearchedProductKeys.details() })
            toast({
                title: 'Produto adicionado na pesquisa'
            })
            setStatus(false)
            setPDV(0)
            setProduct('')
            onClose()
        },
        onError: () => {
            let errorMessage =
                'Não foi possível adicionar o produto. Tente novamente mais tarde.'

            toast({
                title: 'Erro ao adicionar o produto',
                description: errorMessage,
                variant: 'destructive',
            })
        },
    })

    const onSave = async (data: z.infer<typeof schema>) => {
        const options = {
            fileType: "image/webp",
            maxSizeMB: 0.5,
            initialQuality: 0.8,
            useWebWorker: true,
        };
        

        const compressedFile = data.IMAGEM?.[0] ? await imageCompression(data.IMAGEM?.[0], options) : null

        if(online) {
            mutate({
                id: 0,
                SK_PESQUISA: MarketResearch.SK_PESQUISA, 
                SK_CLIENTE: data.SK_CLIENTE, 
                SK_PRODUTO: data.SK_PRODUTO, 
                VL_PRECO_COMPRA: data.VL_PRECO_COMPRA,
                QTD_VOLUME_COMPRA: data.QTD_VOLUME_COMPRA,
                VL_PRECO_VENDA: data.VL_PRECO_VENDA,
                DD_LATITUDE: (currentLocation.current.coords.latitude).toString(),
                DD_LONGITUDE: (currentLocation.current.coords.longitude).toString(),  
                IMAGEM: compressedFile,
                DD_PRODUTO_EXTRA: 0,
                CREATED_AT: new Date().toISOString(),
            })
            
            return
        } 
            
        const setData = {
            SK_PESQUISA: MarketResearch.SK_PESQUISA,
            DS_REVENDA: MarketResearch.DS_REVENDA,
            SK_CLIENTE: data.SK_CLIENTE,
            DS_NOME_FANTASIA: DataPDV?.find(item => item.SK_CLIENTE == data.SK_CLIENTE)?.DS_NOME_FANTASIA,
            SK_PRODUTO: data.SK_PRODUTO,
            DS_PRODUTO: DataProduct?.find(item => item.SK_PRODUTO == data.SK_PRODUTO)?.DS_PRODUTO,
            VL_PRECO_COMPRA: data.VL_PRECO_COMPRA || 0,
            QTD_VOLUME_COMPRA: data.QTD_VOLUME_COMPRA || 0,
            VL_PRECO_VENDA: data.VL_PRECO_VENDA || 0,
            DD_LATITUDE: (currentLocation.current.coords.latitude).toString(),
            DD_LONGITUDE: (currentLocation.current.coords.longitude).toString(),  
            IMAGEM: compressedFile, 
            DD_PRODUTO_EXTRA: 0,
            CREATED_AT: new Date().toISOString(),
        }
        
        await addItem("ProductsFromCustomer",setData)
 
        toast({
                title: 'Sem conexão com a rede',
                description: "Produto adicionado para salvar depois.",
                variant: 'warning',
        })

        setStatus(false)
        setPDV(0)
        setProduct('')
        form.reset()
    }

    useEffect(() => {
        if(online && DataProductFromPDV) {
            setProductSelect(DataProductFromPDV.map(item => {
                return {
                    DS_PRODUTO: item.DS_PRODUTO,
                    SK_PRODUTO: item.SK_PRODUTO
                }
            }))
        }

        if(!online && DataProduct) {
            setProductSelect(DataProduct.map(item => {
                return {
                    DS_PRODUTO: item.DS_PRODUTO,
                    SK_PRODUTO: item.SK_PRODUTO
                }
            }))
        }
    },[isOpen, online, PDV, DataProductFromPDV, DataProduct])

    
    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className='max-w-[600px] overflow-auto'>
                <DialogHeader className='px-2'>
                    <DialogTitle>Informar produto na pesquisa</DialogTitle>
                </DialogHeader>
                <Form {...form}>
                    <form
                        onSubmit={form.handleSubmit(onSubmit)}
                        className="space-y-2 px-2 overflow-hidden overflow-y-auto"
                    >
                        <div className='grid grid-cols-[1fr] gap-2'> 
                             <div
                                className={`fixed top-3 right-3 z-50 px-3 py-1 rounded-full text-xs font-medium
                                    ${online ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}
                                `}
                                >
                                {online ? "Online" : "Offline"}
                            </div>
                            <FormField
                                name="SK_CLIENTE"
                                control={form.control}
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Cliente*</FormLabel>
                                         {!DataPDV && (
                                            <Input 
                                                disabled
                                                placeholder='Carregamento os clientes'
                                            />
                                        )}
                                        {DataPDV && (
                                            <Popover>
                                                <PopoverTrigger asChild>
                                                    <Button
                                                        variant="outline"
                                                        className="justify-start w-full gap-2 text-xs"
                                                    >
                                                        {PDV === 0 ? (
                                                            <TypographyXS className="flex-1 text-start">
                                                                Selecione um cliente
                                                            </TypographyXS>
                                                        ) : (
                                                            <>
                                                                <TypographyXS className="p-1 px-2 overflow-hidden text-xs font-normal text-accent-foreground text-ellipsis">
                                                                    {DataPDV.find(supp => supp.SK_CLIENTE == PDV)?.DS_NOME_FANTASIA}
                                                                </TypographyXS>
                                                            </>
                                                        )}
                                                    </Button>
                                                </PopoverTrigger>
                                                <PopoverContent className="z-50 p-0 h-60 w-96" align="start">
                                                    <VirtualizedCommand
                                                        options={DataPDV.map(item => { return { value: item.SK_CLIENTE.toString(), label: `${item.DS_NOME_FANTASIA} (${item.NK_CLIENTE})` } })}
                                                        placeholder="PDV" 
                                                        selectedValues={{ [`${field.value}`]: true }}
                                                        onSelectionChange={(data) => {
                                                            setPDV(Number(data))
                                                            field.onChange(Number(data))
                                                            if(Number(data) > 0){
                                                                handleSelectProduct()
                                                            }
                                                        }}
                                                    />
                                                </PopoverContent>
                                            </Popover>

                                        )}
                                        <FormMessage />
                                    </FormItem>
                                )}
                            /> 

                            <FormField
                                name="SK_PRODUTO"
                                control={form.control}
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Produto*</FormLabel>
                                        {!ProductSelect && (
                                            <Input 
                                                disabled
                                                placeholder='Selecione um cliente'
                                            />
                                        )}
                                        {ProductSelect && (
                                            <Popover>
                                                <PopoverTrigger asChild>
                                                    <Button
                                                        variant="outline"
                                                        className="justify-start w-full gap-2 text-xs"
                                                    >
                                                        {Product === '' ? (
                                                            <TypographyXS className="flex-1 text-start">
                                                                Selecione um Produto
                                                            </TypographyXS>
                                                        ) : (
                                                            <>
                                                                <TypographyXS className="p-1 px-2 overflow-hidden text-xs font-normal text-accent-foreground text-ellipsis">
                                                                    {ProductSelect.find(supp => supp.SK_PRODUTO == Product)?.DS_PRODUTO}
                                                                </TypographyXS>
                                                            </>
                                                        )}
                                                    </Button>
                                                </PopoverTrigger>
                                                <PopoverContent className="z-50 p-0 h-60 w-96" align="start">
                                                    <VirtualizedCommand
                                                        options={ProductSelect.map(item => { return { value: item.SK_PRODUTO.toString(), label: item?.DS_PRODUTO } })}
                                                        placeholder="Produto" 
                                                        selectedValues={{ [`${field.value}`]: true }}
                                                        onSelectionChange={(data) => {
                                                            setProduct((data))
                                                            field.onChange((data))
                                                        }}
                                                    />
                                                </PopoverContent>
                                            </Popover>

                                        )}
                                        <FormMessage />
                                    </FormItem>
                                )}
                            /> 

                            <FormField
                                name="VL_PRECO_COMPRA"
                                control={form.control}
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Preço de compra (Cx)</FormLabel>
                                        <FormControl>
                                            <Input
                                                {...field}
                                                inputMode="numeric"
                                                placeholder='R$ 0,00'
                                                value={formatCurrencyBR(field.value)}
                                                onChange={(e) =>
                                                    field.onChange(parseCurrencyBR(e.target.value))
                                                }
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                name="QTD_VOLUME_COMPRA"
                                control={form.control}
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Volume em Caixa Semanal (Cx)</FormLabel>
                                        <FormControl>
                                            <Input {...field}  onChange={(e) => field.onChange(parseInt(e.target.value))} type='number'/>
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            /> 

                            <FormField
                                name="VL_PRECO_VENDA"
                                control={form.control}
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Preço de venda (un.)</FormLabel>
                                        <FormControl>
                                            <Input
                                                {...field}
                                                inputMode="numeric"
                                                placeholder='R$ 0,00'
                                                value={formatCurrencyBR(field.value)}
                                                onChange={(e) =>
                                                    field.onChange(parseCurrencyBR(e.target.value))
                                                }
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="IMAGEM"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Imagem do preço do produto</FormLabel>

                                        <FormControl>
                                            <Input
                                            type="file" 
                                            onChange={(e) => {
                                                const files = e.target.files
                                                field.onChange(files) 
                                            }}
                                        />
                                        </FormControl> 
                                        <FormMessage />
                                    </FormItem>
                                )}
                                />

                        </div>    
                        <DialogFooter>
                            <Button
                                type="button"
                                onClick={onClose}
                                variant="ghost"
                            >
                                Cancelar
                            </Button>
                            <Button isLoading={isPending} type="submit">
                                Confirmar
                            </Button>
                        </DialogFooter> 
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    )
}

export default AddProductMarketResearchDialog
